<template>
  <div class="compact-color-swatches">
    <div class="swatches-list">
      <button
        v-for="color in visibleColors"
        :key="color.id"
        type="button"
        class="compact-swatch"
        :class="{ active: isColorActive(color.id) }"
        :aria-label="`Select color ${color.name}`"
        :disabled="disabled"
        @click.stop="onSwatchClick(color.id)"
      >
        <img v-if="color.swatchImage?.filePath" :src="getSwatchUrl(color)" :alt="color.name" class="compact-swatch__img" />
        <span v-else class="compact-swatch__fallback"></span>
      </button>
    </div>
    <span v-if="remainingCount > 0" class="more-colors"> +{{ remainingCount }} </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Color } from '~/declaration/traits';
import useUrlTemplate from '~/composables/useUrlTemplate';

const props = withDefaults(
  defineProps<{
    colors?: Color[];
    maxSwatches?: number;
    selectedColorId?: string | number | null;
    disabled?: boolean;
  }>(),
  {
    colors: () => [],
    maxSwatches: 5,
    selectedColorId: null,
    disabled: false,
  },
);

const emit = defineEmits<(e: 'update:selectedColorId', id: string | number) => void>();

const { productUrlTemplate } = useUrlTemplate();

const getSwatchUrl = (color: Color) => {
  if (!color.swatchImage?.filePath) return '';
  return `${productUrlTemplate}${color.swatchImage.filePath}?op_sharpen=1&wid=21&hei=21&fit=fit,1&$filtersm$`;
};

const isColorActive = (colorId: string | number) => {
  if (!props.selectedColorId) return false;
  return String(colorId) === String(props.selectedColorId);
};

const onSwatchClick = (colorId: string | number) => {
  if (props.disabled) return;
  emit('update:selectedColorId', colorId);
};

const visibleColors = computed(() => props.colors.slice(0, props.maxSwatches));
const remainingCount = computed(() => props.colors.length - props.maxSwatches);
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '@core/csg/src/scss/typography/typography-mixins.scss';

.compact-color-swatches {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.swatches-list {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}

.compact-swatch {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: $white;
  flex-shrink: 0;
  cursor: pointer;

  // Constant footprint to prevent layout shift
  width: 21px;
  height: 21px;
  min-width: 21px;
  min-height: 21px;
  border: 1px solid transparent;
  box-sizing: border-box;

  &.active {
    border: 1px solid $black !important;
    padding: 1px;

    .compact-swatch__img,
    .compact-swatch__fallback {
      width: 100%;
      height: 100%;
      min-width: 0;
      min-height: 0;
    }
  }
}

.compact-swatch__img,
.compact-swatch__fallback {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid $gray-1-color;
  box-sizing: border-box;
  display: block;
}

.compact-swatch__img {
  object-fit: cover;
}

.compact-swatch__fallback {
  background-color: $gray-4-color;
}

.more-colors {
  @include body(small);
  color: $black;
}
</style>


